import { useEffect, useState } from "react";

import ProductionApi, { ShiftWindowDto, ShiftWindowsResponse } from "../services/ProductionApi";
import { DEFAULT_SCHEDULE, WorkScheduleConfig } from "../utilities/workSchedule";

/** Um turno para o seletor (id + nome + janela). */
export interface ShiftInfo {
  id: string;
  name: string;
  start: string;
  end: string;
  startMinute: number;
  endMinute: number;
}

interface Loaded {
  config: WorkScheduleConfig;
  shifts: ShiftInfo[];
}

// Cache de módulo: UMA busca de /Production/shifts compartilhada por todas as telas.
let cached: Loaded | null = null;
let inflight: Promise<Loaded | null> | null = null;

const FALLBACK: Loaded = { config: DEFAULT_SCHEDULE, shifts: [] };

async function fetchShifts(): Promise<Loaded | null> {
  try {
    const resp = await ProductionApi.GetShiftWindows();
    const data = (resp?.data as ShiftWindowsResponse) ?? undefined;
    const raw = (data?.windows ?? []) as ShiftWindowDto[];
    const shifts: ShiftInfo[] = raw
      .filter((w) => Number.isFinite(w.startMinuteOfDay) && Number.isFinite(w.endMinuteOfDay))
      .map((w) => ({
        id: w.id,
        name: w.name,
        start: w.start,
        end: w.end,
        startMinute: w.startMinuteOfDay,
        endMinute: w.endMinuteOfDay,
      }));
    if (!shifts.length) return null; // planta sem turno → fallback, permite retry
    return {
      config: {
        windows: shifts.map((s) => ({ startMinute: s.startMinute, endMinute: s.endMinute })),
        tzOffsetHours: data?.tzOffsetHours ?? DEFAULT_SCHEDULE.tzOffsetHours,
      },
      shifts,
    };
  } catch {
    return null;
  }
}

function useLoaded(): Loaded {
  const [loaded, setLoaded] = useState<Loaded>(cached ?? FALLBACK);

  useEffect(() => {
    let alive = true;
    if (cached) {
      setLoaded(cached);
      return;
    }
    if (!inflight) {
      inflight = fetchShifts().then((c) => {
        if (c) cached = c;
        else inflight = null; // falha/sem turno → libera nova tentativa
        return c;
      });
    }
    void inflight.then((c) => {
      if (alive && c) setLoaded(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  return loaded;
}

/**
 * Janelas de turno da planta (DB-driven) como config para os helpers de horário. Fallback
 * 08:00–17:00 até a resposta chegar / se falhar / planta sem turno.
 */
export default function useWorkSchedule(): WorkScheduleConfig {
  return useLoaded().config;
}

/** Lista de turnos (id + nome + janela) para o seletor de filtro de turno. */
export function useShiftList(): ShiftInfo[] {
  return useLoaded().shifts;
}
